// @ts-check
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styles from './Comment.module.scss';
import Waypoint from 'react-waypoint';
import { connect } from 'react-redux';
import api from 'utils/api';
import Tooltip from './Tooltip';

function initialsForName(name) {
  const names = name.split(' ');

  if (names.length === 0) {
    return '';
  } else if (names.length === 1) {
    return names[0].substring(0, 1).toUpperCase();
  } else {
    // names.length > 1
    return [names[0].substring(0, 1), names[names.length - 1].substring(0, 1)]
      .join('')
      .toUpperCase();
  }
}

/** @typedef {{
  id: string,
  message: string
}} CommentType */

/** @typedef {{
  id: string,
  userName: string,
  userOrganizationName: string,
  createdAt: string,
  comment: CommentType,
  viewed: () => Promise | null,
  isPrivate: boolean
}} CommentPropsType */

/** @typedef {{
  trackCommentViews: boolean,
  apiPrefix: string,
  comment: CommentType
} & CommentPropsType} CommentWrapperPropsType */

/** @augments { Component<CommentWrapperPropsType> } */
class CommentWrapper extends Component {
  constructor(props) {
    super(props);
    this.viewed = this.viewed.bind(this);
  }

  viewed() {
    const { trackCommentViews, apiPrefix, comment } = this.props;
    /**
     * TODO: Debounce this with pushing to an array so that on a
     * page with 30 comments, we only send 1 request with 30 comment ids
     * rather than 30 requests
     */
    if (trackCommentViews) {
      return api.post(`${apiPrefix}/comments/${comment.id}/views`);
    }
    return null;
  }

  render() {
    return <Comment {...this.props} viewed={this.viewed} />;
  }
}

/** @type { React.FC<CommentPropsType> } */
const Comment = (props) => {
  const {
    id,
    userName,
    userOrganizationName,
    createdAt,
    comment,
    viewed,
    isPrivate,
  } = props;

  const cn = [
    styles.Comment,
    'p-2 max-w-3xl rounded-lg',
    isPrivate ? 'bg-gray-100' : 'bg-white border shadow-md',
  ].join(' ');

  return (
    <div className={cn} data-spec-id={`comment-${comment.id}`}>
      <div className={styles.avatar_container}>{initialsForName(userName)}</div>
      <div className={styles.comment_content}>
        <div className={styles.comment_header}>
          <Tooltip
            id={id}
            renderTooltip={() => <span>{userOrganizationName}</span>}
          >
            <span className={styles.bold}>{userName}</span>
          </Tooltip>
          {'  '}
          <span className={styles.timestamp}>{createdAt}</span>
          <span
            className={`font-semibold italic ml-5 ${
              isPrivate ? 'text-purple-400' : 'text-teal-400'
            } text-xs`}
          >
            {isPrivate ? 'Comment Internal Only' : 'Comment Visible to All'}
          </span>
        </div>
        <div className={styles.message}>{comment.message}</div>
        <Waypoint onEnter={viewed} />
      </div>
    </div>
  );
};

Comment.propTypes = {
  comment: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  apiPrefix: state.configuration.apiPrefix,
  trackCommentViews: !!state.configuration.trackCommentViews,
});

export default connect(mapStateToProps)(CommentWrapper);
